import { useState } from 'react';
import { CheckCircle2, CloudUpload, Loader2, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '@higgins/ui';
import { countWaiting, getJob, listOutbox, listRecords, type DeviceRecord } from '../lib/device-db';
import { useDeviceQuery, useOnline } from '../lib/hooks';
import { syncNow } from '../lib/outbox';
import { discardRefused, listRefused, retryRefused } from '../lib/refused';
import { formatDateTime } from '../lib/dates';
import { shortError } from '../lib/short-error';
import { HeldBackList } from './held-back-list';
import { ErrorMessage, Notice } from './message';

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  return formatDateTime(iso);
}

/**
 * Records changed elsewhere, so this device's changes to them are held back, not sent: on any table, so
 * the desk doesn't say "all synced" while an option or review answer waits (#630). Settled in the
 * held-back list (#693). A change CRM refused is set aside separately (listRefused).
 */
const countHeldBack = (records: DeviceRecord[]): number => records.filter((r) => r.state === 'conflict' && !r.refusedReason).length;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Online or not, what's waiting, when it last synced — and a way to push it now. On the job page and the desk (#445). */
export function SyncStatus({ jobId }: { jobId: string }) {
  const online = useOnline();
  const [syncing, setSyncing] = useState(false);
  const refused = useDeviceQuery(() => listRefused(jobId), [jobId]);
  const [discardProblem, setDiscardProblem] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const state = useDeviceQuery(async () => {
    const [waiting, job, outbox, records] = await Promise.all([countWaiting(jobId), getJob(jobId), listOutbox(), listRecords(jobId)]);
    const stuck = outbox.find((entry) => entry.jobId === jobId && entry.lastError);
    return { waiting, lastSyncedAt: job?.lastSyncedAt ?? null, stuckError: stuck?.lastError ?? null, held: countHeldBack(records) };
  }, [jobId]);

  async function sync() {
    setSyncing(true);
    try {
      await syncNow();
    } finally {
      setSyncing(false);
    }
  }

  const waiting = state?.waiting ?? 0;
  const held = state?.held ?? 0;
  // Sticks just under the app's sticky header (its height: useHeaderHeight in _layout), not over it (#652).
  // z-20: above the Take Off's pinned Building/Area cells (z-10) as the table scrolls under it.
  return (
    <>
    <div className="sticky top-[var(--app-header-height,0px)] z-20 -mx-4 mb-3 border-b border-slate-200 bg-white/95 px-3 py-1 text-sm backdrop-blur sm:mx-0 sm:rounded-lg sm:border">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2">
          {online ? (
            waiting + held > 0 ? <CloudUpload className="h-4 w-4 shrink-0 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
          ) : (
            <WifiOff className="h-4 w-4 shrink-0 text-slate-500" />
          )}
          <span className="text-slate-700">
            {online ? 'Online' : 'Offline'} ·{' '}
            {waiting > 0 && <strong className="text-amber-700">{waiting} waiting to sync</strong>}
            {waiting > 0 && held > 0 && ' · '}
            {held > 0 && (
              <button type="button" className="font-bold text-amber-700 underline underline-offset-2" onClick={() => setListOpen((open) => !open)} aria-expanded={listOpen}>
                {held} held back
              </button>
            )}
            {waiting + held === 0 && 'all synced'}
          </span>
          <span className="text-xs text-slate-500">· last synced {timeAgo(state?.lastSyncedAt ?? null)}</span>
        </span>
        <Button size="sm" variant="ghost" className="h-8 shrink-0 px-2 text-xs" onClick={sync} disabled={!online || syncing || waiting === 0}>
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1">Sync now</span>
        </Button>
      </div>
      {held > 0 && !listOpen && (
        <Notice className="mt-1">
          {plural(held, 'change was', 'changes were')} held back because someone else changed the same record in CRM. Tap <strong>{held} held back</strong> to choose which
          version to keep.
        </Notice>
      )}
      {refused && refused.records.length + refused.photos.length > 0 && (
        <Notice className="mt-1">
          <p>
            {refused.records.length + refused.photos.length} change{refused.records.length + refused.photos.length === 1 ? ' wasn’t' : 's weren’t'} saved: {shortError(refused.reason ?? '')} They are kept on this
            device, set aside so everything else still syncs. If your access is being sorted out, try again afterwards; otherwise discard them.
          </p>
          <div className="mt-1 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => void retryRefused(jobId)} disabled={!online}>
              Try again
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!online}
              onClick={() => {
                setDiscardProblem(null);
                discardRefused(jobId).catch((err: unknown) => setDiscardProblem(err instanceof Error ? err.message : String(err)));
              }}
            >
              Discard them
            </Button>
          </div>
        </Notice>
      )}
      {discardProblem && <ErrorMessage className="mt-1">Couldn’t discard them: {shortError(discardProblem)}</ErrorMessage>}
      {state?.stuckError && online && (
        <ErrorMessage className="mt-1">
          Last sync attempt failed: {shortError(state.stuckError)} Your changes are saved on this device and will be sent again by themselves.
        </ErrorMessage>
      )}
    </div>
    {listOpen && <HeldBackList jobId={jobId} onClose={() => setListOpen(false)} />}
    </>
  );
}
